import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Folder, Upload, FolderPlus, History, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useObjects,
  usePresignDownload,
  useDeleteObject,
  useCreateFolder,
  s3ExtractError,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../../S3ConsoleBreadcrumbs';
import { BucketTabs } from '../bucket-subnav';
import { useObjectUpload } from './use-object-upload';
import { ObjectUploadPanel } from './ObjectUploadPanel';
import { ObjectVersionsDialog } from './ObjectVersionsDialog';
import { PrefixBar, NewFolderDialog } from './object-browser-parts';
import { objectColumns, type BrowserRow } from './object-columns';

const leaf = (key: string, prefix: string) => key.slice(prefix.length);

/** Object browser — prefix (folder) navigation, upload, download, delete, new folder, versions. */
export function ObjectBrowser({ serviceId }: { serviceId: string }) {
  const toast = useToast();
  const { bucket = '' } = useParams<{ bucket: string }>();
  const [params, setParams] = useSearchParams();
  const prefix = params.get('prefix') ?? '';

  // S3 listing is forward-only: the server hands back an opaque `next_token` per
  // page, never a total count. We keep the start token of every visited page in
  // a stack so Prev/Next map onto the existing offset-style Pagination control
  // (index N = markerStack[N-1]). Reset when the folder (prefix) changes.
  const [markerStack, setMarkerStack] = useState<string[]>(['']);
  const marker = markerStack[markerStack.length - 1];
  useEffect(() => setMarkerStack(['']), [prefix, bucket]);

  const { data, isLoading } = useObjects(serviceId, bucket, prefix, marker);
  const download = usePresignDownload(serviceId, bucket);
  const deleteObject = useDeleteObject(serviceId, bucket);
  const createFolder = useCreateFolder(serviceId, bucket);
  const { items, uploadFiles, clearFinished, uploading } = useObjectUpload(serviceId, bucket, prefix);

  const fileRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const goPrefix = (p: string) => setParams(p ? { prefix: p } : {});

  const page = markerStack.length;
  const pageCount = page + (data?.is_truncated && data.next_token ? 1 : 0);
  const changePage = (target: number) => {
    if (target > page && data?.next_token) {
      setMarkerStack((s) => [...s, data.next_token as string]);
    } else if (target < page) {
      setMarkerStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
    }
  };

  const rows: BrowserRow[] = [
    ...(data?.common_prefixes ?? []).map((p) => ({
      kind: 'prefix' as const,
      name: leaf(p.prefix, prefix).replace(/\/$/, ''),
      full: p.prefix,
    })),
    ...(data?.contents ?? [])
      .filter((o) => o.key !== prefix) // drop the folder-marker self-entry
      .map((o) => ({ kind: 'object' as const, name: leaf(o.key, prefix), obj: o })),
  ].filter((r) => !search.trim() || r.name.toLowerCase().includes(search.toLowerCase()));

  const handleDownload = (key: string) => {
    download.mutate(key, {
      onSuccess: (url) => window.open(url, '_blank', 'noopener'),
      onError: (e) => toast.error(s3ExtractError(e, 'Failed to get download link')),
    });
  };

  const handleFiles = (files?: FileList | null) => {
    if (!files || files.length === 0) return;
    void uploadFiles(Array.from(files));
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = () => {
    if (!deleteKey) return;
    deleteObject.mutate(deleteKey, {
      onSuccess: () => { toast.success('Object deleted'); setDeleteKey(null); },
      onError: (e) => { toast.error(s3ExtractError(e, 'Failed to delete object')); setDeleteKey(null); },
    });
  };

  const columns = objectColumns({
    onOpenPrefix: goPrefix,
    onDownload: handleDownload,
    onDelete: setDeleteKey,
  });

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: 'Buckets', slug: 'buckets' }, { label: bucket }]} />}
        title={bucket}
        description="Browse, upload and manage objects."
        actions={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
            <Button variant="secondary" startIcon={<History size={15} />} onClick={() => setShowVersions(true)}>
              Versions
            </Button>
            <Button variant="secondary" startIcon={<FolderPlus size={15} />} onClick={() => setShowNewFolder(true)}>
              New Folder
            </Button>
            <Button variant="primary" startIcon={<Upload size={15} />} onClick={() => fileRef.current?.click()}>
              Upload
            </Button>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
          </div>
        }
      />

      <BucketTabs serviceId={serviceId} bucket={bucket} />

      <div
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{ position: 'relative' }}
      >
        <PrefixBar prefix={prefix} onNavigate={goPrefix} />

        <Toolbar
          start={
            <Input
              placeholder="Search in this folder…"
              startSlot={<Search size={15} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          }
        />

        <ObjectUploadPanel items={items} uploading={uploading} onClear={clearFinished} />

        {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => (r.kind === 'prefix' ? `p:${r.full}` : `o:${r.obj.key}`)}
          onRowClick={(r) => r.kind === 'prefix' && goPrefix(r.full)}
          emptyState={
            <EmptyState
              icon={<Folder size={20} />}
              title="No objects yet"
              description="Upload a file or create a folder to get started."
              actions={
                <Button size="sm" startIcon={<Upload size={14} />} onClick={() => fileRef.current?.click()}>
                  Upload
                </Button>
              }
            />
          }
        />
      )}

        {!isLoading && pageCount > 1 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={changePage}
            showFirstLast={false}
            summary={`Page ${page}`}
          />
        )}

        {dragOver && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--vn-space-2)',
              borderRadius: 'var(--vn-radius-lg)',
              border: '2px dashed var(--vn-accent-solid)',
              background: 'var(--vn-accent-subtle)',
              color: 'var(--vn-accent-text)',
              fontWeight: 'var(--vn-font-weight-medium)',
              pointerEvents: 'none',
            }}
          >
            <Upload size={18} /> Drop files to upload
          </div>
        )}
      </div>

      {showNewFolder && (
        <NewFolderDialog
          prefix={prefix}
          creating={createFolder.isPending}
          onClose={() => setShowNewFolder(false)}
          onCreate={(full) =>
            createFolder.mutate(full, {
              onSuccess: () => { toast.success('Folder created'); setShowNewFolder(false); },
              onError: (e) => toast.error(s3ExtractError(e, 'Failed to create folder')),
            })
          }
        />
      )}

      {showVersions && (
        <ObjectVersionsDialog serviceId={serviceId} bucket={bucket} prefix={prefix} onClose={() => setShowVersions(false)} />
      )}

      <ConfirmDialog
        open={deleteKey !== null}
        onOpenChange={(o) => !o && setDeleteKey(null)}
        title="Delete Object"
        description={`This permanently deletes "${deleteKey}". This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteObject.isPending}
        onConfirm={handleDelete}
      />
    </Container>
  );
}
